import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import {useMemo} from 'react';

import {AssetHuman} from '../../interfaces/AssetHuman';

type OptionType = {
  id: number | string;
  nickname?: string | null;
  external_id?: number | null;
};

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'name'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'sx'
> & {
  value?: string | number;
  assetHumans?: AssetHuman[];
  onChange?: (value: string | number) => void;
};

const AssetHumanSelectGantt = ({
  value,
  disabled,
  fullWidth,
  size,
  assetHumans = [],
  onChange,
  ...props
}: Props) => {
  const options = useMemo<OptionType[]>(() => {
    return [
      {
        id: 'exclude',
        nickname: 'None',
      },
      {
        id: -1,
        nickname: 'All',
      },
      ...assetHumans,
    ];
  }, [assetHumans]);

  const selectedOption =
    options.find((i) => {
      if (value === 'exclude') {
        return i.id === 'exclude';
      }
      if (value === -1) {
        return i.id === -1;
      }
      return i.external_id === value;
    }) ?? null;

  const getOptionLabel = (option: OptionType) => {
    if (option.id === 'exclude') {
      return 'None';
    }
    if (option.id === -1) {
      return 'All';
    }
    return option.nickname ?? `Employee #${option.external_id}`;
  };

  return disabled ? (
    <TextField
      {...props}
      value={selectedOption ? getOptionLabel(selectedOption) : ''}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
    />
  ) : (
    <Autocomplete
      value={selectedOption ?? undefined}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      size={size}
      disableClearable
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <TextField label="Employee" {...props} {...params} />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {getOptionLabel(option)}
          </li>
        );
      }}
      onChange={(_, option) => {
        if (!option) {
          return;
        }
        if (option.id === 'exclude') {
          onChange?.('exclude');
        } else if (option.id === -1) {
          onChange?.(-1);
        } else {
          onChange?.(option.external_id ?? 0);
        }
      }}
    />
  );
};

export default AssetHumanSelectGantt;
